import { samePreviewBackground } from '@/helpers/title-preview'
import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
// Static import (was a per-navigation dynamic import in beforeEach — an
// awaited microtask + module-registry hit on EVERY route change). nav.js
// only imports pinia, so there's no circular-import risk with the router.
import { isCinematicRoute, useNavStore } from "@/stores/nav";

const routes = [
  {
    path: "/",
    name: "home",
    component: () => import("../views/LibraryAllView.vue"),
  },
  {
    // Put.io source browser. Canonical path is `/sources/putio` to
    // mirror `/sources/plex/...` and `/sources/jellyfin/...` — each
    // connected source has its own /sources/<name> page with a
    // matching layout (LibrarySourceTabs strip + DiscoverTabs section
    // tabs + per-section poster shelves). The legacy `/library` and
    // `/library/putio` paths are kept as aliases so existing
    // bookmarks + the header nav don't break.
    path: "/sources/putio",
    alias: ["/library/putio"],
    name: "library",
    component: () => import("../views/LibraryBrowseView.vue"),
  },
  {
    // `/library` defaults to the cross-source "All Libraries" aggregator
    // rather than the Put.io-only browser, so the Library nav + bookmarks
    // land on the unified view. The Put.io browser stays at /sources/putio
    // (+ /library/putio alias), reachable via the "Put.io" source tab.
    path: "/library",
    redirect: { name: "library-all" },
  },
  {
    // Cross-source aggregator view -- Put.io + every reachable Plex
    // server in one scroll. Sits leftmost in the LibrarySourceTabs
    // strip. Spec: docs/research/unified-library-search.md (## All
    // Libraries view spec, 2026-05-22). Gated by the baseline
    // `library` permission; the per-server Plex data inside the view
    // is fetched through the already-`use-external-sources`-gated
    // /api/sources/plex/recently-added endpoint, which fails closed
    // for invites without that grant.
    path: "/library/all",
    name: "library-all",
    component: () => import("../views/LibraryAllView.vue"),
    meta: { requiresPermission: "library" },
  },
  {
    // Legacy alias for the cinematic home. Renders the same HomeView but
    // force-enables TV mode on mount (used by PWA shortcuts, Android TV
    // launcher, smart-TV UA auto-redirect). Phone-shaped touch devices
    // with an active remote session get bounced to /remote/<id>.
    path: "/tv",
    name: "tv",
    component: () => import("../views/TvHomeView.vue"),
    meta: { cinematic: true },
  },
  {
    // Downloads lives under /library/* in the nav: it's the "what's
    // transferring into my library" surface. The `library-downloads`
    // route name is the canonical link target; the bare /downloads
    // path below is kept as an alias for back-compat with old
    // bookmarks / shared links.
    path: "/library/downloads",
    name: "downloads",
    component: () => import("../views/DownloadsView.vue"),
    meta: { requiresPermission: "add-transfers" },
  },
  {
    // Back-compat alias for the old /downloads URL.
    path: "/downloads",
    redirect: { name: "downloads" },
    meta: { public: true },
  },
  {
    path: "/media/:id",
    name: "media",
    component: () => import("../views/MediaView.vue"),
    props: true,
  },
  {
    path: "/browse/:type/:id",
    name: "browse-media",
    component: () => import("../views/BrowseMediaView.vue"),
    props: true,
    // Cinematic so the view owns its own padding -- the backdrop hero
    // runs full-bleed in both desktop and TV densities.
    meta: { requiresTierFull: true, cinematic: true },
  },
  {
    // Top-level Discover. Three sibling routes share the same view but
    // different `meta.mediaType` — the tab strip on the page swaps between
    // them. The route segment is the source of truth for the active tab;
    // there is no `?type=` query param anymore.
    path: "/discover",
    name: "discover",
    component: () => import("../views/DiscoverView.vue"),
    meta: { requiresPermission: "discover", mediaType: "all" },
  },
  {
    path: "/discover/movies",
    name: "discover-movies",
    component: () => import("../views/DiscoverView.vue"),
    meta: { requiresPermission: "discover", mediaType: "movie" },
  },
  {
    path: "/discover/tv",
    name: "discover-tv",
    component: () => import("../views/DiscoverView.vue"),
    meta: { requiresPermission: "discover", mediaType: "tv" },
  },
  {
    path: "/person/:id(\\d+)",
    name: "person",
    component: () => import("../views/PersonView.vue"),
    // Cinematic so the backdrop hero runs full-bleed -- the view
    // manages its own gutters via .person-view__hero and __body.
    meta: { requiresTierFull: true, cinematic: true },
  },
  {
    // Monitoring lives under /discover/* now -- the page IS part of
    // the Discover family in the user's mental model (the in-page
    // sub-nav already surfaces it as a tab alongside Home / Movies
    // / TV Shows). The canonical path is /discover/monitoring (the
    // user-facing rename of the old "Watchlist" feature, #381); the
    // bare /watchlist + the old /discover/watchlist below are kept as
    // back-compat redirects. The route `name` stays 'watchlist' --
    // it's the INTERNAL identifier for the torrent-monitoring list,
    // referenced by every `{ name: 'watchlist' }` call site.
    path: "/discover/monitoring",
    name: "watchlist",
    component: () => import("../views/WatchlistView.vue"),
    meta: { requiresPermission: "discover" },
  },
  {
    // Back-compat redirect for the pre-rename canonical path.
    path: "/discover/watchlist",
    redirect: { name: "watchlist" },
    meta: { public: true },
  },
  {
    // Back-compat alias for the old top-level /watchlist URL.
    path: "/watchlist",
    redirect: { name: "watchlist" },
    meta: { public: true },
  },
  {
    // "My List" — a pure save-for-later bookmark list (separate from the
    // torrent-monitoring Watchlist above). Lives in the same /discover/*
    // family and surfaces as a tab alongside Home / Movies / TV Shows /
    // Watchlist. Same wrapper as the watchlist route (NOT cinematic).
    path: "/discover/my-list",
    name: "my-list",
    component: () => import("../views/MyListView.vue"),
    meta: { requiresPermission: "discover" },
  },
  {
    // "New Episodes" — recent drops from the shows the user follows
    // (Monitoring ∪ series progress). Same /discover/* family + wrapper as
    // My List / Monitoring. Reads TMDB, so it needs the discover permission.
    path: "/discover/new-episodes",
    name: "new-episodes",
    component: () => import("../views/NewEpisodesView.vue"),
    meta: { requiresPermission: "discover" },
  },
  {
    path: "/link-device",
    name: "link-device",
    component: () => import("../views/LinkDeviceView.vue"),
    meta: { requiresPermission: "link-tv" },
  },
  {
    path: "/watch-party/:roomId",
    name: "watch-party",
    component: () => import("../views/WatchPartyView.vue"),
    props: true,
    meta: { public: true },
  },
  {
    // Phone-as-remote landing page. Tries to auto-join the household's
    // active TV session if the phone is already authenticated; otherwise
    // surfaces "no TV active" or "unlock first" guidance.
    path: "/remote",
    name: "remote-home",
    component: () => import("../views/RemoteHomeView.vue"),
    meta: { public: true },
  },
  {
    // Phone-as-remote target. Public — the random sessionId in the URL is
    // the capability, same model as /watch-party/:roomId.
    path: "/remote/:sessionId",
    name: "remote",
    component: () => import("../views/RemoteView.vue"),
    props: true,
    meta: { public: true },
  },
  {
    path: "/play/torbox",
    name: "torbox-play",
    component: () => import("../views/TorboxPlayView.vue"),
    // magnet + title come from query string; declared as props for clarity.
    // season/episode/file are optional hints for a MULTI-FILE torrent (season
    // pack): they pick one episode out of the pack instead of opening the
    // picker. season+episode survive TorBox re-adding the magnet; `file` is a
    // TorBox file id and only holds while that torrent stays cached.
    props: (route) => ({
      magnet: route.query.magnet || "",
      title: route.query.title || "",
      season: route.query.season ?? null,
      episode: route.query.episode ?? null,
      file: route.query.file ?? null,
    }),
    // Not cinematic at the route level -- the view itself decides
    // per-density: windowed page chrome on desktop, full-screen
    // cinematic only when tvMode is on. Same approach for /media
    // and /live-tv/<id>.
    // TorBox playback is folded into the discover permission --
    // anyone who can browse Discover can also instant-play.
    meta: { requiresPermission: "discover" },
  },
  {
    // YouTube app home. Replaces the old search-only landing page with
    // a real section surface: subscriptions, curated live channels,
    // suggested videos, plus the same query-driven search UI.
    path: "/youtube",
    name: "youtube-home",
    component: () => import("../views/YoutubeSearchView.vue"),
    meta: { requiresTierFull: true },
  },
  {
    // YouTube playback spike — bypasses the YouTube player so the proxy-
    // resolved progressive MP4 plays directly in our <video> without
    // YouTube's ad layer. Accept the 11-char video id only.
    path: "/youtube/:videoId([A-Za-z0-9_-]{11})",
    name: "youtube-play",
    component: () => import("../views/YoutubeView.vue"),
    props: true,
    meta: { requiresTierFull: true },
  },
  {
    // YouTube channel detail page. Opens when the user clicks a card
    // on the homepage "Latest on YouTube" shelf. Shows the channel
    // logo + name + description + a LIVE-now CTA (when applicable)
    // + a grid of recent uploads. Each recent upload routes to
    // /youtube/<videoId> for playback.
    path: "/youtube/channel/:handle([A-Za-z0-9._-]{1,40})",
    name: "youtube-channel",
    component: () => import("../views/YoutubeChannelView.vue"),
    props: true,
    meta: { requiresTierFull: true },
  },
  {
    // Back-compat search path. Uses the same section component as
    // /youtube so existing links still work, but the page now opens as
    // a full YouTube surface rather than a bare search box.
    path: "/youtube/search",
    name: "youtube-search",
    component: () => import("../views/YoutubeSearchView.vue"),
    meta: { requiresTierFull: true },
  },
  {
    // Curated Live TV homepage: Free Mirrors rail + Last Watched +
    // Favorites grid. The full 13k-channel catalogue moved to
    // /live-tv/browse so this view stays scannable.
    path: "/live-tv",
    name: "live-tv",
    component: () => import("../views/LiveTvView.vue"),
    meta: { requiresPermission: "live-tv" },
  },
  {
    // World Cup section (Feature 72): schedule + live scores + watch/download
    // recent games. Baseline access — the schedule/scores are viewable by any
    // unlocked user; the Watch/Download actions reuse the existing TorBox /
    // Put.io flows.
    path: "/world-cup",
    name: "world-cup",
    component: () => import("../views/WorldCupView.vue"),
    meta: {},
  },
  {
    // Full catalogue browser. Filter bar + group chips + search + the
    // "tap the star to add to favorites" UX. This is what /live-tv used
    // to be before the curated homepage took over the root path.
    // Registered BEFORE /live-tv/:channelId so the literal `browse`
    // segment doesn't get matched as a channelId.
    path: "/live-tv/browse",
    name: "live-tv-browse",
    component: () => import("../views/LiveTvBrowseView.vue"),
    meta: { requiresPermission: "live-tv" },
  },
  {
    // Player view: TV mode renders a cinematic fixed-inset shell that
    // ignores wrapper padding anyway, and desktop renders a windowed
    // MediaPlayerShell that NEEDS the wideRoutes wrapper padding
    // (px-4 sm:px-6 py-4) to not run flush to the viewport edges.
    // Slim-header hiding in TV mode is already handled by App.vue's
    // explicit `live-tv-player` exclusion in showSlimHeader.
    path: "/live-tv/:channelId",
    name: "live-tv-player",
    component: () => import("../views/LiveTvPlayerView.vue"),
    props: true,
    meta: { requiresPermission: "live-tv" },
  },
  {
    // Playlist management (add / refresh / delete) plus EPG-URL editing.
    path: "/settings/live-tv",
    name: "settings-live-tv",
    component: () => import("../views/LiveTvSettingsView.vue"),
    meta: { requiresPermission: "live-tv" },
  },
  {
    // YouTube subscription manager (Home/Discover shelves). Distinct
    // from /settings/live-tv -- subscriptions surface latest uploads,
    // not 24/7 live broadcasts.
    path: "/settings/youtube",
    name: "settings-youtube",
    component: () => import("../views/SettingsYoutubeView.vue"),
    meta: { requiresPermission: "discover" },
  },
  {
    // Search indexers (Jackett + built-in providers). Admin only --
    // the underlying IndexerSettings component talks to admin-scoped
    // endpoints, so the route guard mirrors /admin/* routes.
    path: "/settings/indexers",
    name: "settings-indexers",
    component: () => import("../views/IndexerSettingsPageView.vue"),
    meta: { requiresAdmin: true },
  },
  {
    // Streaming-provider preferences for Discover (Netflix, Apple TV+,
    // Disney+). Region + Language live inline on /settings — only the
    // provider grid moved here. Same permission as the rest of the
    // Discover surface area.
    path: "/settings/providers",
    name: "settings-providers",
    component: () => import("../views/ProvidersSettingsPageView.vue"),
    meta: { requiresPermission: "discover" },
  },
  {
    path: "/settings",
    name: "settings",
    component: () => import("../views/SettingsView.vue"),
  },
  {
    path: "/locked",
    name: "locked",
    component: () => import("../views/LockedView.vue"),
    meta: { public: true },
  },
  {
    // Feature 75: temporary share link. PUBLIC by design — the whole point is
    // that a recipient with no invite can open it. The token in the URL is the
    // credential, and the server scopes it to a single item.
    path: "/share/:token",
    name: "share",
    component: () => import("../views/ShareView.vue"),
    meta: { public: true },
  },
  {
    // Phone-side approval page for the TV-pair-login flow. PUBLIC because
    // the visitor may not be signed in yet — the view itself routes them
    // to /locked first if they're anonymous, then comes back here once
    // they've redeemed their invite.
    path: "/pair-login/:sessionId",
    name: "pair-login",
    component: () => import("../views/PairLoginApproveView.vue"),
    props: true,
    meta: { public: true },
  },
  {
    path: "/unlock-error",
    name: "unlock-error",
    component: () => import("../views/UnlockErrorView.vue"),
    meta: { public: true },
  },
  {
    // Plex OAuth return page. PUBLIC because the popup might not carry
    // our device cookie depending on the browser's SameSite posture
    // for the plex.tv redirect; the view itself never calls any
    // authenticated endpoint — it just postMessages the opener and
    // closes. See `src/stores/sources.js` connectPlexFlow() for the
    // settings-side orchestration.
    path: "/sources/connect/plex/return",
    name: "sources-plex-return",
    component: () => import("../views/SourcesPlexReturnView.vue"),
    meta: { public: true },
  },
  {
    // Browse a connected Plex server's libraries. Single view handles
    // both list-libraries mode (no ?section) and items-grid mode
    // (?section=<sectionId>). Gated on `use-external-sources` —
    // playback territory, same permission as the player route.
    // Feature 27.
    path: "/sources/plex/:accountId/:serverId",
    // alias `/library/plex/:accountId/:serverId` is the new canonical
    // path (aligned with /library/putio). Legacy /sources/... stays
    // wired so deep links from settings / shared invites don't 404.
    alias: "/library/plex/:accountId/:serverId",
    name: "sources-plex-browse",
    component: () => import("../views/PlexBrowseView.vue"),
    props: true,
    meta: {
      requiresPermission: "use-external-sources",
    },
  },
  {
    // Plex player — direct playback from a Plex server. Gated on the
    // `use-external-sources` permission (Feature 26) since this is a
    // playback surface, not a server-management surface. Uses the same
    // MediaPlayerShell (desktop) / CinematicPlayerShell (TV mode) pair
    // as /media, /play/torbox, /youtube/<id>, /live-tv/<id> -- so the
    // route is NOT meta.cinematic (that would suppress the wrapper
    // padding the windowed shell relies on for its desktop gutters).
    // Per-density chrome is handled inside the view.
    path: "/player/plex/:accountId/:serverId/:ratingKey",
    name: "player-plex",
    component: () => import("../views/PlayerPlexView.vue"),
    props: true,
    meta: {
      requiresPermission: "use-external-sources",
    },
  },
  {
    // Jellyfin/Emby player — direct playback from a Jellyfin server.
    // Same `use-external-sources` gate as Plex (Feature 26). Same
    // not-meta.cinematic rationale as player-plex above.
    path: "/player/jellyfin/:serverId/:itemId",
    name: "player-jellyfin",
    component: () => import("../views/PlayerJellyfinView.vue"),
    props: true,
    meta: {
      requiresPermission: "use-external-sources",
    },
  },
  {
    // Self-service device manager scoped by invite token. Reached either
    // by the server redirect from /unlock/:token when the invite has
    // hit its device_limit, or pushed by LockedView when the JSON
    // unlock attempt returns the same error. Capability is the raw
    // token in the URL — same model as /unlock/:token itself. Lives
    // under /unlock-devices/ rather than /unlock/ to keep Vite's
    // /unlock/* → :3001 dev proxy from intercepting the SPA route.
    path: "/unlock-devices/:token",
    name: "unlock-device-limit",
    component: () => import("../views/UnlockDeviceLimitView.vue"),
    props: true,
    meta: { public: true },
  },
  {
    path: "/admin/tokens",
    name: "admin-tokens",
    component: () => import("../views/AdminTokensView.vue"),
    meta: { requiresAdmin: true },
  },
  {
    path: "/admin/devices/:tokenId",
    name: "admin-devices",
    component: () => import("../views/AdminDevicesView.vue"),
    meta: { requiresAdmin: true },
    props: true,
  },
  {
    path: "/admin/tokens/:tokenId/featured",
    name: "admin-featured",
    component: () => import("../views/AdminFeaturedView.vue"),
    meta: { requiresAdmin: true },
    props: true,
  },
  {
    // Feature 21 — auto-organize toggle, status, history, undo.
    // Same admin-guard pattern as /admin/tokens and /admin/devices/:tokenId.
    path: "/admin/library",
    name: "admin-library",
    component: () => import("../views/AdminLibraryView.vue"),
    meta: { requiresAdmin: true },
  },
  {
    // Feature 75: review + revoke temporary share links.
    path: "/admin/shares",
    name: "admin-shares",
    component: () => import("../views/AdminSharesView.vue"),
    meta: { requiresAdmin: true },
  },
  {
    // Feature 76: PaymoneyWubby stream archive browser (admin only).
    path: "/admin/wubby",
    name: "admin-wubby",
    component: () => import("../views/AdminWubbyView.vue"),
    meta: { requiresAdmin: true },
  },
  {
    // Generic crash page. Reached either by router.onError (chunk
    // load failures, route guard exceptions) or by manual push from
    // anywhere that wants a graceful "something broke" landing.
    // Public so even unauthenticated state can land here.
    path: "/error",
    name: "error",
    component: () => import("../views/ErrorView.vue"),
    meta: { public: true },
  },
  {
    // Catch-all 404. MUST be the last route entry. pathMatch(.*)* is
    // the canonical vue-router 4 pattern for unknown paths. Public
    // so a wrong URL doesn't bounce a logged-out user into the auth
    // flow first.
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("../views/NotFoundView.vue"),
    meta: { public: true },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  // Scroll behavior:
  // - Back / forward (savedPosition present): restore the user's last
  //   scroll position on that route so going Back returns them to
  //   where they were. Applied ASYNC via waitForScrollRoom — shelf pages
  //   load content after mount, and restoring while the document is
  //   still short clamps the scroll to the top (the "lost my place"
  //   bug). Waiting until the page can host the saved offset (or 1.5s)
  //   makes the restore stick; behavior:"instant" so it doesn't animate
  //   or fight the TV-mode focus restore's own scrollIntoView.
  // - Forward nav (savedPosition null): scroll to the top so clicking
  //   "Home" from deep inside a page actually lands at the top of the
  //   new view.
  // Returns null only for in-page hash navigation handled by the
  // anchor itself.
  scrollBehavior(to, from, savedPosition) {
    if (samePreviewBackground(to, from)) return false
    if (savedPosition) {
      return import("../helpers/scroll-restore.js").then(
        ({ waitForScrollRoom }) =>
          waitForScrollRoom({
            getScrollHeight: () => document.documentElement.scrollHeight,
            viewportHeight: window.innerHeight,
            savedTop: savedPosition.top,
          }).then(() => ({ ...savedPosition, behavior: "instant" })),
      );
    }
    // Settings hashes select a category rather than an in-page anchor.
    if (to.name === 'settings' && /^#(watching|appearance|discovery|sources|device)$/.test(to.hash)) return { top: 0 };
    if (to.hash) return { el: to.hash };
    return { top: 0 };
  },
});

function landingFor(auth, query = {}, hash = '') {
  if (auth.canAccessLibrary) return { name: 'library-all', query, hash };
  if (auth.canBrowseTorrents) return { name: 'discover', query, hash };
  if (auth.canBrowseLiveTv) return { name: 'live-tv', query, hash };
  return { name: 'locked' };
}

// Gate every non-public navigation behind the auth store. Public routes
// (watch-party, locked, unlock-error) pass through without hydrating, so
// anonymous users can hit them without triggering a server round-trip.
//
// IMPORTANT: useAuthStore() is called INSIDE the guard body. Pinia must be
// installed on the app before the first navigation, which happens because
// main.js does `app.use(pinia)` before `app.use(router)`.
router.beforeEach(async (to, from) => {
  // Record the route the user came from BEFORE entering a cinematic
  // player. Each player's back button reads this so we land on the
  // exact origin even when intermediate router.replace / route-guard
  // pushes have polluted browser history.
  try {
    if (
      isCinematicRoute(to.name) &&
      !isCinematicRoute(from.name) &&
      from.fullPath &&
      from.fullPath !== "/"
    ) {
      useNavStore().rememberReturn(to.name, from.fullPath);
    }
  } catch {
    /* nav store optional -- never block navigation on a missing store */
  }

  if (to.meta.public) return true;

  const authStore = useAuthStore();
  await authStore.hydrate();

  if (to.meta.requiresAdmin) {
    if (authStore.status !== "admin") {
      return { name: "locked" };
    }
    return true;
  }

  if (authStore.status === "unlocked" || authStore.status === "admin") {
    if (to.name === "home") return landingFor(authStore, to.query, to.hash);
    // Granular permission gate. Routes can declare:
    //   meta: { requiresPermission: 'live-tv' }
    // Resolve denied destinations to the first permitted browsing section.
    const required = to.meta.requiresPermission;
    if (typeof required === "string" && !authStore.hasPermission(required)) {
      return landingFor(authStore);
    }
    // Back-compat alias for routes that haven't migrated yet. Maps to
    // `discover` (the closest 1:1 with the old "full-tier" gate).
    if (to.meta.requiresTierFull && !authStore.canBrowseTorrents) {
      return landingFor(authStore);
    }
    return true;
  }

  // Locked — avoid redirect loop.
  if (to.name === "locked") return true;
  return { name: "locked" };
});

// Route-level error boundary. Chunk-load failures (browser dropped a
// stale lazy import during a deploy) and uncaught exceptions in route
// component setup land here. We surface a friendly /error page with
// a short reference instead of leaving the user on a blank route.
router.onError((err, to) => {
  // ChunkLoadError after a deploy -- the simplest recovery is to
  // hard-reload at the target URL, which fetches the fresh bundle.
  const message = String(err?.message || err || "");
  const isChunkLoad =
    /Loading chunk \d+ failed|ChunkLoadError|Failed to fetch dynamically imported module/i.test(
      message,
    );
  if (isChunkLoad && to?.fullPath) {
    window.location.href = to.fullPath;
    return;
  }
  console.error("[router.onError]", err);
  const code = isChunkLoad ? "chunk_load_failed" : "route_error";
  router
    .replace({
      name: "error",
      query: {
        code,
        message: isChunkLoad
          ? "A part of the app failed to load. Refresh to fetch the latest version."
          : "The page hit an error while loading. Try again or head home.",
      },
    })
    .catch(() => {});
});

export default router;
